import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import {
  PageContainer,
  PaymentCard,
  PaymentBrandPanel,
  CheckoutPanel,
  Header,
  Title,
  Subtitle,
  SummaryBox,
  AmountRow,
  AmountLabel,
  Amount,
  DetailRow,
  DetailLabel,
  DetailValue,
  AlertBox,
  PrimaryButton,
  SecondaryButton,
  Footer,
  PoweredBy,
  SecureMark,
} from './PaymentVisuals';

const InfoBox = styled.div`
  margin-bottom: 20px;
  padding: 14px 16px;
  color: #7c5814;
  font-size: 10px;
  line-height: 1.55;
  background: #fff8e8;
  border: 1px solid #ead49d;
  border-radius: 3px;
  overflow-wrap: anywhere;
  
  strong {
    display: block;
    margin-bottom: 8px;
  }
  
  ul {
    margin: 8px 0 0 17px;
    padding: 0;
  }
  
  li {
    margin-bottom: 4px;
  }
`;

const DevelopmentDetails = styled.details`
  margin-bottom: 20px;
  color: #7c5814;
  font-size: 10px;
  line-height: 1.55;
  background: #fff8e8;
  border: 1px solid #ead49d;
  border-radius: 3px;

  summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 13px 15px;
    font-weight: 800;
    cursor: pointer;
    list-style: none;
  }

  summary::-webkit-details-marker { display: none; }
  summary::after { content: "+"; font-size: 16px; font-weight: 500; }
  &[open] summary::after { content: "−"; }
  ${InfoBox} { margin: 0; border: 0; border-top: 1px solid #ead49d; }
`;

const DevelopmentActions = styled.div`
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid #e1e5eb;

  > p { margin: 0 0 10px; color: #8b929e; font-size: 8px; letter-spacing: .06em; text-transform: uppercase; }
  > div { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
  button { min-width: 0; min-height: 40px; padding: 0 8px; font-size: 9px; }
`;

const RestoPayment = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const autoStartAttempted = useRef(false);

  // Extract parameters
  const amount = searchParams.get('amount');
  const paymentType = searchParams.get('paymentType');
  const parentOrigin = decodeURIComponent(searchParams.get('origin') || '');
  const mode = searchParams.get('mode');
  const planId = searchParams.get('planId');
  const planName = searchParams.get('planName');
  const restaurantId = searchParams.get('restaurantId');
  const restaurantName = searchParams.get('restaurantName');
  const customerName = searchParams.get('customerName');
  const customerPhone = searchParams.get('customerPhone');
  const customerEmail = searchParams.get('customerEmail');
  const orderId = searchParams.get('orderId');
  const explicitReferenceId = searchParams.get('referenceId');
  const source = searchParams.get('source') || 'resto';
  // Pre-initiated flow: authenticated client already called /initiate and passes the result
  const precomputedPhonePeUrl = searchParams.get('phonePeUrl');
  const precomputedMerchantTxnId = searchParams.get('merchantTransactionId');

  // Map frontend paymentType to backend paymentPurpose
  const getPaymentPurpose = () => {
    switch (paymentType) {
      case 'subscription': return 'Plan Subscription';
      case 'whatsapp_recharge': return 'WhatsApp Credits';
      case 'order': return 'Ecommerce Order';
      default: return paymentType || 'General Payment';
    }
  };

  // Auto-redirect when payment was pre-initiated by the authenticated client
  useEffect(() => {
    if (!precomputedPhonePeUrl || !precomputedMerchantTxnId) return;

    const decodedUrl = decodeURIComponent(precomputedPhonePeUrl);

    // Notify parent window with the already-known merchantTransactionId
    if (window.opener && parentOrigin) {
      window.opener.postMessage({
        type: 'PAYMENT_INITIATED',
        merchantTransactionId: precomputedMerchantTxnId,
        amount: Number(amount),
        paymentType,
        planId,
      }, parentOrigin);
    }

    // Store for callback page (same pattern as normal initiate flow)
    localStorage.setItem(`payment_origin_${precomputedMerchantTxnId}`, parentOrigin);
    localStorage.setItem(`payment_data_${precomputedMerchantTxnId}`, JSON.stringify({
      amount,
      paymentType,
      restaurantName,
      planName,
      planId,
      orderId,
      customerName,
      customerPhone,
      customerEmail,
      source,
      mode: 'popup',
    }));

    // Redirect to PhonePe directly — no API call needed
    window.location.href = decodedUrl;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Send result to parent window
  const sendResultToParent = (result) => {
    if (window.opener && parentOrigin) {
      console.log('Sending result to parent:', result);
      window.opener.postMessage(result, parentOrigin);
    } else {
      console.warn('Cannot send result - no parent window or origin');
    }
  };

  // Handle cancel
  const handleCancel = () => {
    sendResultToParent({
      type: 'PAYMENT_RESULT',
      status: 'CANCELLED',
      message: 'Payment cancelled by user',
    });
    window.close();
  };

  // Initiate payment
  const initiatePayment = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Use the Willovate Resto API (Azure production backend)
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.willovate.in';

      // Prepare payment data according to backend API documentation
      const paymentData = {
        planId: planId ? Number(planId) : undefined,
        planName: planName || undefined,
        restaurantId: restaurantId ? Number(restaurantId) : undefined,
        amount: Number(amount),
        mobileNumber: customerPhone || undefined,
        redirectUrl: `${window.location.origin}/payment/callback`,
        customerName: customerName || undefined,
        customerEmail: customerEmail || undefined,
        paymentPurpose: getPaymentPurpose(),
        // referenceId identifies the target entity for this payment type:
        // - WhatsApp credits: restaurantId (so backend can top up the right business)
        // - Ecommerce orders: orderId (so backend can confirm the order after payment)
        referenceId: explicitReferenceId
                   || (paymentType === 'whatsapp_recharge' ? (restaurantId || '')
                   : paymentType === 'order' ? (orderId || '')
                   : undefined),
        source,
      };
      
      console.log('Initiating payment with payload:', paymentData);
      
      const response = await fetch(`${apiUrl}/api/payment/phonepe/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(paymentData)
      });
      
      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      
      console.log('Payment initiation response:', result);
      
      if (result.success && result.data?.redirectUrl) {
        // Store parent origin and transaction details for callback
        const merchantTransactionId = result.data.merchantTransactionId;

        // Notify parent window early — before PhonePe redirect.
        // This lets the parent store the merchantTransactionId so it can verify
        // status even if the popup closes without sending a PAYMENT_RESULT message.
        if (window.opener && parentOrigin) {
          window.opener.postMessage({
            type: 'PAYMENT_INITIATED',
            merchantTransactionId,
            amount: Number(amount),
            paymentType,
            planId,
          }, parentOrigin);
        }

        localStorage.setItem(`payment_origin_${merchantTransactionId}`, parentOrigin);
        localStorage.setItem(`payment_data_${merchantTransactionId}`, JSON.stringify({
          amount,
          paymentType,
          restaurantName,
          planName,
          planId,
          orderId,
          customerName,
          customerPhone,
          customerEmail,
          source,
          mode: 'popup'
        }));
        
        // Redirect to PhonePe within popup
        window.location.href = result.data.redirectUrl;
      } else {
        setError(result.message || 'Failed to initiate payment');
        setLoading(false);
      }
    } catch (err) {
      console.error('Payment initiation error:', err);
      
      // Show helpful error message
      if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        setError('Backend API not available. Please set up the PhonePe backend API first. See PHONEPE_RESTO_INTEGRATION.md for setup instructions.');
      } else {
        setError(`Payment initiation failed: ${err.message}`);
      }
      setLoading(false);
    }
  };

  // Test success (for development)
  const handleTestSuccess = () => {
    sendResultToParent({
      type: 'PAYMENT_RESULT',
      status: 'SUCCESS',
      transactionId: `TEST_${Date.now()}`,
      merchantTransactionId: `RESTO_${Date.now()}`,
      amount: Number(amount),
      message: 'Test payment successful',
    });
    setSuccess(true);
    setTimeout(() => window.close(), 2000);
  };

  // Test failure (for development)
  const handleTestFailure = () => {
    sendResultToParent({
      type: 'PAYMENT_RESULT',
      status: 'FAILED',
      transactionId: `TEST_${Date.now()}`,
      merchantTransactionId: `RESTO_${Date.now()}`,
      amount: Number(amount),
      message: 'Test payment failed',
    });
    setTimeout(() => window.close(), 2000);
  };

  // Validate required parameters
  useEffect(() => {
    if (!amount || !paymentType || !parentOrigin || mode !== 'popup') {
      setError('Invalid payment parameters');
    }
  }, [amount, paymentType, parentOrigin, mode]);

  useEffect(() => {
    if (
      autoStartAttempted.current ||
      precomputedPhonePeUrl ||
      precomputedMerchantTxnId ||
      !amount ||
      !paymentType ||
      !parentOrigin ||
      mode !== 'popup'
    ) return;

    autoStartAttempted.current = true;
    void initiatePayment();
  }, [amount, mode, parentOrigin, paymentType, precomputedMerchantTxnId, precomputedPhonePeUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PageContainer>
      <PaymentCard>
        <PaymentBrandPanel gateway="PhonePe" />
        <CheckoutPanel>
          <Header>
            <span>Checkout / PhonePe</span>
            <Title>Opening secure payment</Title>
            <Subtitle>PhonePe opens automatically. Use retry only if the gateway could not start.</Subtitle>
          </Header>

        {/* Development Mode Info */}
        {process.env.NODE_ENV === 'development' && (
          <DevelopmentDetails>
            <summary>Development environment</summary>
            <InfoBox>
              <strong>Backend API Required</strong>
              <p>To make real payments, you need to set up the PhonePe backend API:</p>
              <ul>
                <li>Set REACT_APP_API_URL in .env file</li>
                <li>Create POST /api/phonepe/initiate endpoint</li>
                <li>See PHONEPE_RESTO_INTEGRATION.md for details</li>
              </ul>
              <p style={{ marginTop: '8px', marginBottom: 0 }}>
                <strong>For testing:</strong> Use "Test Success" or "Test Failure" below.
              </p>
            </InfoBox>
          </DevelopmentDetails>
        )}

        {/* Payment Summary */}
        <SummaryBox>
          <AmountRow>
            <AmountLabel>Amount to Pay</AmountLabel>
            <Amount>₹{amount}</Amount>
          </AmountRow>
          
          {planName && (
            <DetailRow>
              <DetailLabel>Plan</DetailLabel>
              <DetailValue>{planName}</DetailValue>
            </DetailRow>
          )}
          
          {restaurantName && (
            <DetailRow>
              <DetailLabel>Restaurant</DetailLabel>
              <DetailValue>{restaurantName}</DetailValue>
            </DetailRow>
          )}
          
          {orderId && (
            <DetailRow>
              <DetailLabel>Order ID</DetailLabel>
              <DetailValue>{orderId}</DetailValue>
            </DetailRow>
          )}
          
          {customerName && (
            <DetailRow>
              <DetailLabel>Customer</DetailLabel>
              <DetailValue>{customerName}</DetailValue>
            </DetailRow>
          )}
          
          <DetailRow>
            <DetailLabel>Payment Type</DetailLabel>
            <DetailValue style={{ textTransform: 'capitalize' }}>
              {paymentType}
            </DetailValue>
          </DetailRow>
        </SummaryBox>

        {/* Error Message */}
        {error && (
          <AlertBox>
            <XCircle size={20} />
            <span>{error}</span>
          </AlertBox>
        )}

        {/* Success Message */}
        {success && (
          <AlertBox $success>
            <CheckCircle size={20} />
            <span>Payment successful! Closing window...</span>
          </AlertBox>
        )}

        {/* Action Buttons */}
        {!success && (
          <>
            {(loading || error) && (
              <PrimaryButton
                onClick={initiatePayment}
                disabled={loading || !amount || !paymentType || !parentOrigin || mode !== 'popup'}
              >
                {loading ? (
                  <>
                    <Loader size={20} className="animate-spin" />
                    <span>Opening PhonePe...</span>
                  </>
                ) : (
                  <span>Try PhonePe again</span>
                )}
              </PrimaryButton>
            )}

            <SecondaryButton
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </SecondaryButton>

            {/* Development Test Buttons */}
            {process.env.NODE_ENV === 'development' && (
              <DevelopmentActions>
                <p>
                  Development Test Actions:
                </p>
                <div>
                  <SecondaryButton
                    onClick={handleTestSuccess}
                  >
                    <CheckCircle size={16} />
                    Test Success
                  </SecondaryButton>
                  <SecondaryButton
                    onClick={handleTestFailure}
                  >
                    <XCircle size={16} />
                    Test Failure
                  </SecondaryButton>
                </div>
              </DevelopmentActions>
            )}
          </>
        )}

        {/* Footer */}
        <Footer>
          <SecureMark>Your payment is secure and encrypted</SecureMark>
          <PoweredBy>
            <span>Powered by</span>
            <strong>PhonePe</strong>
          </PoweredBy>
        </Footer>
        </CheckoutPanel>
      </PaymentCard>
    </PageContainer>
  );
};

export default RestoPayment;
